import { useEffect, useState } from "react";
import axios from "axios";
import { Box } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import Loading from "../component/Loading";
import Header from "./Header";
import MyProfile from "./MyProfile";
import "../css/style.css";

import ConstList from "../utils/ConstList";
import { useMediaQueryContext } from "../component/MediaQueryProvider";

import { LoginUserAtom } from "../stores/LoginUserAtom";
import { ItemRecoilState } from "../stores/ItemAtom";
import { maintenanceState } from "../stores/MaintenanceAtom";

function MyPage() {
  // ==========================================================
  // Auth0の認証状態
  //
  // isLoading:
  //   Auth0がログイン状態を確認している間はtrueになる。
  //
  // isAuthenticated:
  //   ログイン済みの場合はtrueになる。
  // ==========================================================
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();

  // ==========================================================
  // 画面内のデータ取得状態
  //
  // loading:
  //   マイページ情報の取得中かどうか。
  //
  // error:
  //   API取得に失敗した場合に表示するメッセージ。
  // ==========================================================
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ==========================================================
  // 画面サイズ判定
  //
  // PC・タブレットとスマートフォンで、
  // マイページ見出しのサイズを切り替える。
  // ==========================================================
  const { isTabletSite, isPcSite } = useMediaQueryContext();

  // ==========================================================
  // ログインユーザー情報
  //
  // email:
  //   GetMyProfile.phpへ送信し、対象ユーザーを特定する。
  //
  // accessToken:
  //   ログインユーザー情報の初期設定が完了したかを
  //   判定するために使用する。
  // ==========================================================
  const loginUserData = useRecoilValue(LoginUserAtom);

  // ==========================================================
  // マイページで使用するユーザーデータの保存先
  //
  // APIから取得したユーザー情報を、
  // ItemRecoilState.userへ保存する。
  // ==========================================================
  const setItemRecoilData = useSetRecoilState(ItemRecoilState);

  // ==========================================================
  // メンテナンス情報の保存先
  //
  // getMaintenance.phpから取得した情報を、
  // ヘッダーなどで使用できるようRecoilへ保存する。
  // ==========================================================
  const setMaintenance = useSetRecoilState(maintenanceState);

  // ==========================================================
  // 初回表示時にバージョンとメンテナンス情報を確認する
  //
  // この処理はMyPageが表示された最初の1回だけ実行する。
  // コンポーネントが破棄された後に状態更新しないよう、
  // cancelledフラグを使用している。
  // ==========================================================
  useEffect(() => {
    let cancelled = false;

    // 現在のフロントエンドバージョンを確認する
    ConstList.versionCheck();

    // メンテナンス情報をAPIから取得する
    async function getMaintenance() {
      try {
        const response = await axios.get(
          ConstList.getApiUrl() + "GetMaintenance.php",
          ConstList.AXIOS_CONFIG,
        );

        if (cancelled) {
          return;
        }

        setMaintenance(response.data);
      } catch (caughtError) {
        // メンテナンス情報の取得失敗は、
        // マイページ本体の表示を止めずコンソールへ記録する
        if (axios.isAxiosError(caughtError)) {
          console.error("メンテナンス情報の取得に失敗しました。", {
            status: caughtError.response?.status,
            responseData: caughtError.response?.data,
            message: caughtError.message,
          });
        } else {
          console.error("メンテナンス情報の取得に失敗しました。", caughtError);
        }
      }
    }

    void getMaintenance();

    // MyPageが閉じられた場合、
    // その後の状態更新を行わない
    return () => {
      cancelled = true;
    };
  }, [setMaintenance]);

  // ==========================================================
  // ログインユーザーのマイページ情報を取得する
  //
  // 次の条件がすべて揃ってからAPIを呼び出す。
  //
  // ・Auth0の認証確認が完了している
  // ・Auth0でログイン済み
  // ・アクセストークンが取得済み
  // ・メールアドレスが取得済み
  //
  // emailが空の状態でGetMyProfile.phpを呼ばないことが重要。
  // ==========================================================
  useEffect(() => {
    // Auth0が認証状態を確認している間は実行しない
    if (isAuthLoading) {
      return;
    }

    // 未ログインの場合は実行しない
    if (!isAuthenticated) {
      return;
    }

    // Recoilの値を変数へ取り出し、空白を除去する
    const accessToken = loginUserData.accessToken?.trim() ?? "";

    const email = loginUserData.email?.trim() ?? "";

    // ログインユーザー情報の初期化が終わるまで待つ
    //
    // 以前のコードではaccessTokenしか確認していなかったため、
    // emailが空の状態でAPIを呼ぶ可能性があった。
    if (!accessToken || !email) {
      setLoading(true);
      return;
    }

    let cancelled = false;

    // ログインユーザーのプロフィールを取得する
    async function getMyProfile() {
      // 再取得時は、以前のエラーを一度消して
      // ローディング表示へ戻す
      setLoading(true);
      setError("");

      try {
        const response = await axios.post(
          ConstList.getApiUrl() + "GetMyProfile.php",
          {
            email,
          },

          // axios.postの第3引数へ共通設定を渡す
          //
          // Content-Type、CORS、Cookieなどの設定が
          // AXIOS_CONFIGに含まれている場合に反映される。
          ConstList.AXIOS_CONFIG,
        );

        if (cancelled) {
          return;
        }

        // APIからユーザー情報が返っているか確認する
        //
        // HTTP 200でもdataが存在しない場合は、
        // 正常なプロフィール取得として扱わない。
        if (
          !response.data ||
          response.data.data === undefined ||
          response.data.data === null
        ) {
          console.error(
            "GetMyProfile.phpの応答形式が正しくありません。",
            response.data,
          );

          throw new Error("APIからユーザー情報が返されませんでした。");
        }

        // 取得したユーザー情報をRecoilへ保存する
        setItemRecoilData((currentInput) => ({
          ...currentInput,
          user: response.data.data,
        }));

        // 以前のAPI取得でエラーになっていた場合でも、
        // 今回成功した時点でエラー表示を消す
        setError("");
      } catch (caughtError) {
        if (cancelled) {
          return;
        }

        // Axiosエラーの場合は、
        // HTTPステータスやPHP側の応答もコンソールへ出す
        if (axios.isAxiosError(caughtError)) {
          console.error("ユーザー情報の取得に失敗しました。", {
            requestUrl: ConstList.getApiUrl() + "GetMyProfile.php",
            requestEmail: email,
            status: caughtError.response?.status,
            responseData: caughtError.response?.data,
            message: caughtError.message,
          });
        } else {
          console.error("ユーザー情報の取得に失敗しました。", caughtError);
        }

        setError("ユーザー情報の取得に失敗しました。");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void getMyProfile();

    // emailなどが変化して再取得が始まった場合、
    // 古いリクエストによる状態更新を防止する
    return () => {
      cancelled = true;
    };
  }, [
    isAuthLoading,
    isAuthenticated,
    loginUserData.accessToken,
    loginUserData.email,
    setItemRecoilData,
  ]);

  // ==========================================================
  // Auth0の認証確認中
  //
  // ログイン済みか未ログインか確定するまで、
  // ページ本体を表示せずローディング表示にする。
  // ==========================================================
  if (isAuthLoading) {
    return <Loading />;
  }

  // ==========================================================
  // 未ログインの場合
  //
  // 通常はログイン前画面からマイページへ遷移しないが、
  // URLを直接入力された場合に備えて表示する。
  // ==========================================================
  if (!isAuthenticated) {
    return <h2>ログインしていません。</h2>;
  }

  // ==========================================================
  // ログイン済みの場合
  //
  // ログイン後ヘッダーとマイページ内容を表示する。
  // ==========================================================
  return (
    <>
      <Header />

      <Box
        sx={{
          padding: "20px",
          pt: 10,
        }}
      >
        {/* 画面幅に合わせて見出しサイズを変更する */}
        {isPcSite || isTabletSite ? <h3>マイページ</h3> : <h4>マイページ</h4>}

        {/* ユーザー情報の取得状態に応じて表示を切り替える */}
        {loading ? (
          <Loading />
        ) : error ? (
          <Box
            role="alert"
            sx={{
              py: 2,
              color: "error.main",
              fontWeight: 600,
            }}
          >
            {error}
          </Box>
        ) : (
          <MyProfile />
        )}
      </Box>
    </>
  );
}

export default MyPage;
